import { useRef, useState } from "react";
import { toast } from "sonner";
import QRCode from "react-qr-code";
import { ArrowSquareOut, Check, Images, ImageSquare, Palette, Printer, Trash, UploadSimple } from "@phosphor-icons/react";
import { api, adminHeaders, fileUrl, roomUrl } from "@/lib/api";
import { compressImage, compressImageHQ } from "@/lib/compress";
import { ExtraLinksEditor } from "@/components/admin/ExtraLinksEditor";

const GK_VERSIONS = ["Ori", ...Array.from({ length: 20 }, (_, i) => `Revisi ${i + 1}`)];

const STATUS_DEFS = [
    { field: "qr_printed", label: "QR Diprint" },
    { field: "rendering_deployed", label: "Rendering di Lapangan" },
    { field: "order_veneer_hpl", label: "Veneer + HPL Diorder" },
    { field: "order_hardware", label: "Hardware Diorder" },
    { field: "rendering_workshop", label: "Rendering di Workshop" },
    { field: "pola_warna", label: "Upload Pola Warna", auto: true },
    { field: "order_finishing", label: "Finishing Diorder" },
];

const PRODUCTION_OPTIONS = [
    { value: "belum_diproduksi", label: "Belum Diproduksi" },
    { value: "sedang_diproduksi", label: "Sedang Diproduksi" },
    { value: "finishing_budhi", label: "Sedang di-Finishing — Budhi" },
    { value: "finishing_cibogo", label: "Sedang di-Finishing — Cibogo" },
    { value: "siap_kirim", label: "Siap Kirim" },
    { value: "terkirim", label: "Barang Terkirim" },
    { value: "selesai", label: "Selesai Install" },
];

export function RoomCard({ room, onChanged }) {
    const fileRef = useRef(null);
    const polaRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [uploadingPola, setUploadingPola] = useState(false);
    const [meLink, setMeLink] = useState(room.me_link || "");
    const [gkLink, setGkLink] = useState(room.gambar_kerja_link || "");
    const [gkVersion, setGkVersion] = useState(room.gambar_kerja_version || "");
    const [extraLinks, setExtraLinks] = useState(room.extra_links || []);
    const [saving, setSaving] = useState(false);
    const [statusBusy, setStatusBusy] = useState("");

    const toggleStatus = async (field, value) => {
        setStatusBusy(field);
        try {
            await api.patch(`/admin/rooms/${room.id}/status`, { field, value }, { headers: adminHeaders() });
            await onChanged();
        } catch {
            toast.error("Gagal mengubah status");
        } finally {
            setStatusBusy("");
        }
    };

    const setProductionStatus = async (value) => {
        setStatusBusy("production_status");
        try {
            await api.patch(`/admin/rooms/${room.id}/production-status`, { value }, { headers: adminHeaders() });
            await onChanged();
        } catch {
            toast.error("Gagal mengubah status produksi");
        } finally {
            setStatusBusy("");
        }
    };

    const uploadRendering = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const compressed = await compressImage(file);
            const form = new FormData();
            form.append("file", compressed, "rendering.jpg");
            await api.post(`/admin/rooms/${room.id}/rendering`, form, { headers: adminHeaders() });
            toast.success("Rendering terunggah (terkompresi)");
            await onChanged();
        } catch {
            toast.error("Gagal mengunggah rendering");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const deleteRendering = async (path) => {
        try {
            await api.delete(`/admin/rooms/${room.id}/renderings`, { data: { path }, headers: adminHeaders() });
            toast.success("Rendering dihapus");
            await onChanged();
        } catch {
            toast.error("Gagal menghapus rendering");
        }
    };

    const uploadPolaWarna = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploadingPola(true);
        try {
            for (const file of files) {
                const compressed = await compressImageHQ(file);
                const form = new FormData();
                form.append("file", compressed, "pola-warna.jpg");
                await api.post(`/admin/rooms/${room.id}/pola-warna`, form, { headers: adminHeaders() });
            }
            toast.success(`${files.length} foto pola warna terunggah (kualitas tinggi)`);
            await onChanged();
        } catch {
            toast.error("Gagal mengunggah pola warna");
        } finally {
            setUploadingPola(false);
            e.target.value = "";
        }
    };

    const saveLinks = async () => {
        if (gkLink.trim() && !gkVersion) {
            toast.error("Pilih versi gambar kerja dulu (Ori / Revisi)");
            return;
        }
        setSaving(true);
        try {
            await api.put(
                `/admin/rooms/${room.id}`,
                { name: room.name, description: room.description || "", me_link: meLink, gambar_kerja_link: gkLink, gambar_kerja_version: gkLink.trim() ? gkVersion : "", extra_links: extraLinks },
                { headers: adminHeaders() }
            );
            toast.success("Tersimpan");
            await onChanged();
        } catch {
            toast.error("Gagal menyimpan");
        } finally {
            setSaving(false);
        }
    };

    const deleteRoom = async () => {
        if (!window.confirm(`Hapus ruangan "${room.name}"?`)) return;
        try {
            await api.delete(`/admin/rooms/${room.id}`, { headers: adminHeaders() });
            toast.success("Ruangan dihapus");
            await onChanged();
        } catch {
            toast.error("Gagal menghapus ruangan");
        }
    };

    const prodStatus = room.production_status || "belum_diproduksi";
    const warnProduksi =
        prodStatus === "sedang_diproduksi" &&
        (!room.qr_printed || !room.rendering_deployed || !room.gambar_kerja_link || !room.order_veneer_hpl || !room.order_hardware);
    const warnFinishing =
        (prodStatus === "finishing_budhi" || prodStatus === "finishing_cibogo") &&
        (!room.rendering_workshop || !room.order_finishing || (room.pola_warna?.length || 0) === 0);
    const warnReasons = warnProduksi
        ? [
              !room.qr_printed && "QR belum diprint",
              !room.rendering_deployed && "rendering belum di lapangan",
              !room.gambar_kerja_link && "gambar kerja belum ada",
              !room.order_veneer_hpl && "veneer+HPL belum diorder",
              !room.order_hardware && "hardware belum diorder",
          ].filter(Boolean)
        : warnFinishing
          ? [
                !room.rendering_workshop && "rendering belum di workshop",
                !room.order_finishing && "bahan finishing belum diorder",
                (room.pola_warna?.length || 0) === 0 && "pola warna belum di-upload",
            ].filter(Boolean)
          : [];

    return (
        <div
            className={`border bg-white ${
                prodStatus === "selesai"
                    ? "border-green-500 bg-green-50/50"
                    : warnReasons.length > 0
                      ? "border-red-300 bg-red-50/40"
                      : "border-border"
            }`}
            data-testid={`room-card-${room.id}`}
        >
            <div className="flex items-start justify-between p-4 border-b border-border">
                <div>
                    <h3 className="font-serif text-xl sm:text-2xl tracking-tight font-medium" data-testid={`room-name-${room.id}`}>
                        {room.name}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">{room.photo_count} foto service</p>
                    {prodStatus === "selesai" && (
                        <p className="text-[10px] font-bold tracking-widest uppercase text-green-700 mt-1" data-testid={`room-selesai-badge-${room.id}`}>
                            Selesai Install
                        </p>
                    )}
                    {warnReasons.length > 0 && (
                        <p className="text-[10px] font-bold tracking-widest uppercase text-red-700 mt-1" data-testid={`room-warning-${room.id}`}>
                            Perlu perhatian: {warnReasons.join(" • ")}
                        </p>
                    )}
                </div>
                <button
                    data-testid={`room-delete-${room.id}`}
                    onClick={deleteRoom}
                    className="text-muted-foreground hover:text-destructive transition-colors"
                    title="Hapus ruangan"
                >
                    <Trash size={18} weight="light" />
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2">
                <div className="p-4 border-b sm:border-b-0 sm:border-r border-border">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">
                        Rendering {room.renderings?.length > 0 && <span className="normal-case font-normal">({room.renderings.length})</span>}
                    </p>
                    {room.renderings?.length > 0 ? (
                        <div className="grid grid-cols-2 gap-2" data-testid={`room-renderings-${room.id}`}>
                            {room.renderings.map((path, i) => (
                                <div key={path} className="relative group aspect-[4/3] bg-secondary overflow-hidden border border-border">
                                    <img
                                        src={fileUrl(path)}
                                        alt={`Rendering ${room.name} ${i + 1}`}
                                        className="w-full h-full object-cover"
                                        data-testid={`room-rendering-${room.id}-${i}`}
                                    />
                                    <button
                                        data-testid={`room-rendering-delete-${room.id}-${i}`}
                                        onClick={() => deleteRendering(path)}
                                        className="absolute top-1 right-1 bg-white/90 border border-border p-1 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Hapus rendering"
                                    >
                                        <Trash size={14} weight="bold" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="aspect-[4/3] bg-secondary flex items-center justify-center border border-border">
                            <ImageSquare size={32} weight="light" className="text-muted-foreground" />
                        </div>
                    )}
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadRendering} data-testid={`room-rendering-input-${room.id}`} />
                    <button
                        data-testid={`room-rendering-upload-${room.id}`}
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        className="mt-3 w-full border border-primary text-primary py-2 text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        <UploadSimple size={16} weight="bold" />
                        {uploading ? "Mengunggah..." : "Tambah Rendering"}
                    </button>
                </div>

                <div className="p-4 flex flex-col">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">Tautan Google Drive</p>
                    <label className="text-xs text-muted-foreground">Gambar ME / Electrical</label>
                    <input
                        data-testid={`room-me-link-${room.id}`}
                        value={meLink}
                        onChange={(e) => setMeLink(e.target.value)}
                        placeholder="https://drive.google.com/..."
                        className="mt-1 mb-3 border border-border px-3 py-2 text-sm outline-none focus:border-primary transition-colors bg-transparent"
                    />
                    <label className="text-xs text-muted-foreground">Gambar Kerja (internal)</label>
                    <input
                        data-testid={`room-gk-link-${room.id}`}
                        value={gkLink}
                        onChange={(e) => setGkLink(e.target.value)}
                        placeholder="https://drive.google.com/..."
                        className="mt-1 border border-border px-3 py-2 text-sm outline-none focus:border-primary transition-colors bg-transparent"
                    />
                    <div className="mt-2 mb-1 flex items-center gap-2">
                        <label className="text-xs text-muted-foreground shrink-0">Versi Gambar Kerja</label>
                        <select
                            data-testid={`room-gk-version-${room.id}`}
                            value={gkVersion}
                            onChange={(e) => setGkVersion(e.target.value)}
                            className="flex-1 border border-border px-3 py-2 text-sm outline-none focus:border-primary transition-colors bg-white"
                        >
                            <option value="">— Pilih versi —</option>
                            {GK_VERSIONS.map((v) => (
                                <option key={v} value={v}>
                                    {v}
                                </option>
                            ))}
                        </select>
                    </div>
                    <ExtraLinksEditor links={extraLinks} onChange={setExtraLinks} testIdPrefix={`room-extra-link-${room.id}`} />
                    <button
                        data-testid={`room-save-links-${room.id}`}
                        onClick={saveLinks}
                        disabled={saving}
                        className="mt-3 bg-primary text-primary-foreground py-2 text-sm font-medium hover:bg-[#9c4a2a] transition-colors disabled:opacity-50"
                    >
                        {saving ? "Menyimpan..." : "Simpan"}
                    </button>
                </div>
            </div>

            <div className="p-4 border-t border-border flex items-center justify-between gap-3 flex-wrap" data-testid={`room-progress-${room.id}`}>
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">
                    Foto Progress Lapangan {(room.progress_count || 0) > 0 && <span className="normal-case font-normal">({room.progress_count})</span>}
                </p>
                <a
                    data-testid={`room-progress-open-${room.id}`}
                    href={`/a/${room.id}/progress`}
                    className="border border-border px-3 py-2 text-xs hover:border-primary hover:text-primary transition-colors flex items-center gap-1.5"
                >
                    <Images size={14} weight="light" /> Lihat Progress
                </a>
            </div>

            <div className="p-4 border-t border-border" data-testid={`room-status-${room.id}`}>
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-2">Status Produksi</p>
                <select
                    data-testid={`room-production-status-${room.id}`}
                    value={room.production_status || "belum_diproduksi"}
                    onChange={(e) => setProductionStatus(e.target.value)}
                    disabled={statusBusy === "production_status"}
                    className={`mb-3 w-full border px-3 py-2 text-sm outline-none transition-colors bg-white disabled:opacity-50 ${
                        (room.production_status || "belum_diproduksi") === "siap_kirim"
                            ? "border-green-600 text-green-700 font-medium"
                            : "border-border focus:border-primary"
                    }`}
                >
                    {PRODUCTION_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
                <div className="flex flex-wrap gap-2">
                    {STATUS_DEFS.map(({ field, label, auto }) => {
                        const on = auto ? (room.pola_warna?.length || 0) > 0 : !!room[field];
                        return (
                            <button
                                key={field}
                                data-testid={`room-status-${field}-${room.id}`}
                                onClick={() => !auto && toggleStatus(field, !on)}
                                disabled={auto || statusBusy === field}
                                title={auto ? "Otomatis tercentang bila pola warna sudah diupload" : on ? "Klik untuk tandai belum" : "Klik untuk tandai sudah"}
                                className={`text-xs px-2.5 py-1.5 border transition-colors flex items-center gap-1.5 disabled:opacity-50 ${
                                    on
                                        ? "border-green-600 text-green-700 bg-green-50"
                                        : "border-border text-muted-foreground hover:border-primary hover:text-primary"
                                } ${auto ? "cursor-default" : ""}`}
                            >
                                <span
                                    className={`w-3.5 h-3.5 border flex items-center justify-center text-[10px] ${
                                        on ? "bg-green-600 border-green-600 text-white" : "border-muted-foreground"
                                    }`}
                                >
                                    {on && <Check size={10} weight="bold" />}
                                </span>
                                {label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="flex items-center gap-4 p-4 border-t border-border">
                <div className="flex flex-col items-center gap-1 shrink-0">
                    <div className="bg-white p-1 border border-border" data-testid={`room-qr-${room.id}`}>
                        <QRCode value={roomUrl(room.id)} size={72} />
                    </div>
                    {room.short_code && (
                        <p className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground" data-testid={`room-short-code-${room.id}`}>
                            {room.short_code}
                        </p>
                    )}
                </div>
                <div className="flex-1 flex flex-wrap gap-2">
                    <a
                        data-testid={`room-open-page-${room.id}`}
                        href={`/a/${room.id}`}
                        className="border border-border px-3 py-2 text-sm hover:border-primary hover:text-primary transition-colors flex items-center gap-2"
                    >
                        <ArrowSquareOut size={16} weight="light" /> Buka Halaman (Mode Admin)
                    </a>
                    <a
                        data-testid={`room-print-label-${room.id}`}
                        href={`/print/${room.id}`}
                        className="border border-border px-3 py-2 text-sm hover:border-primary hover:text-primary transition-colors flex items-center gap-2"
                    >
                        <Printer size={16} weight="light" /> Cetak Label QR
                    </a>
                    <input
                        ref={polaRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={uploadPolaWarna}
                        data-testid={`room-pola-input-${room.id}`}
                    />
                    <button
                        data-testid={`room-pola-upload-${room.id}`}
                        onClick={() => polaRef.current?.click()}
                        disabled={uploadingPola}
                        className="border border-border px-3 py-2 text-sm hover:border-primary hover:text-primary transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                        <Palette size={16} weight="light" /> {uploadingPola ? "Mengunggah..." : "Upload Pola Warna"}
                    </button>
                </div>
            </div>
        </div>
    );
}
